
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  getMarketingCampaign,
  getCampaignMetrics,
  listCampaignRecipients,
  listMarketingTemplates,
} from "../../api/marketingCampaigns";
import { getPortalSettings } from "../../api/clientPortalAdmin";
import InfoTooltip from "../../components/common/InfoTooltip";
import { formatDateTimeInTimeZone } from "../../utils/datetime";
import { getCurrentSalonId, getCurrentSalonName } from "../../utils/currentSalon";
import { CAMPAIGN_GOALS, CAMPAIGN_TOOLTIPS } from "../../constants/marketingCampaigns";
import "../MarketingSectionCompact.css";

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const pct = (value) => `${Math.round((Number(value || 0) || 0) * 100)}%`;

const buildCampaignLink = (salonId, campaignId) => {
  if (!salonId || !campaignId || typeof window === "undefined") return "";
  const base = window.location.origin;
  const params = new URLSearchParams({
    utm_source: "sbin",
    utm_medium: "email",
    utm_campaign: String(campaignId),
  });
  return `${base}/portal/salon/${salonId}/book?${params.toString()}`;
};

function ShareToSocialPanel({ salonId, salonName, campaignId, template, socialLinks }) {
  const campaignLink = buildCampaignLink(salonId, campaignId);
  const subject = template?.subject || "New offer";
  const postText = `${salonName || "Our salon"}: ${subject}. Book here: ${campaignLink}`;

  const handleCopy = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      // Ignore clipboard errors.
    }
  };

  const links = [
    { label: "Facebook", url: socialLinks?.facebook_url },
    { label: "Instagram", url: socialLinks?.instagram_url },
    { label: "TikTok", url: socialLinks?.tiktok_url },
    { label: "YouTube", url: socialLinks?.youtube_url },
    { label: "Google Reviews", url: socialLinks?.google_review_url },
  ];

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
      <div>
        <div className="text-sm font-semibold text-slate-900">Share to Social</div>
        <div className="text-xs text-slate-500">
          Copy a post or open your salon social links.
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => handleCopy(postText)}
          className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-white"
        >
          Copy social post text
        </button>
        <button
          type="button"
          onClick={() => handleCopy(campaignLink)}
          className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-white"
        >
          Copy campaign link
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {links.map((link) =>
          link.url ? (
            <a
              key={link.label}
              href={link.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-800"
            >
              {link.label}
            </a>
          ) : (
            <span
              key={link.label}
              className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-400"
            >
              {link.label}
            </span>
          )
        )}
      </div>
    </div>
  );
}

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const salonId = getCurrentSalonId(user);
  const salonName = getCurrentSalonName(user);
  const salonTimeZone = user?.salon_timezone || "America/Toronto";

  const [campaign, setCampaign] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [socialLinks, setSocialLinks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const [campaignData, metricsData, recipientData, templateRows, portalSettings] =
        await Promise.all([
          getMarketingCampaign(id, token),
          getCampaignMetrics(id, token),
          listCampaignRecipients(id, { limit: 200 }, token),
          salonId ? listMarketingTemplates(salonId, token) : Promise.resolve([]),
          salonId ? getPortalSettings(salonId, token).catch(() => null) : null,
        ]);
      setCampaign(campaignData || null);
      setMetrics(metricsData || null);
      setRecipients(Array.isArray(recipientData) ? recipientData : []);
      setTemplates(Array.isArray(templateRows) ? templateRows : []);
      setSocialLinks(portalSettings?.portal_settings || null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load campaign.");
    } finally {
      setLoading(false);
    }
  }, [id, token, salonId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const template = useMemo(() => {
    if (!campaign?.template_id) return null;
    return templates.find((row) => String(row.id) === String(campaign.template_id));
  }, [campaign, templates]);

  const goalLabel = useMemo(() => {
    if (!campaign?.goal) return "";
    return CAMPAIGN_GOALS.find((g) => g.value === campaign.goal)?.label || campaign.goal;
  }, [campaign]);

  const metricCards = [
    { label: "Sent", value: metrics?.sent_count ?? 0 },
    { label: "Delivered", value: metrics?.delivered_count ?? 0 },
    { label: "Opens", value: metrics?.open_count ?? 0 },
    { label: "Clicks", value: metrics?.click_count ?? 0 },
    { label: "Bookings", value: metrics?.bookings_count ?? 0 },
    {
      label: "Revenue",
      value: currency.format(Number(metrics?.total_revenue || 0)),
    },
    { label: "Reviews", value: metrics?.reviews_count ?? 0 },
  ];

  return (
    <div className="marketingSectionShell">
      <div className="marketingSectionWide marketing-section-wide marketing-section-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Campaign Detail
            </div>
            <button
              type="button"
              onClick={() => navigate("/manager/marketing/campaigns")}
              className="rounded-full border border-white/70 px-4 py-2 text-xs md:text-sm font-semibold text-white hover:bg-white/10"
            >
              Back to campaigns
            </button>
          </div>

          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            {loading && <div className="text-xs text-slate-500">Loading campaign...</div>}

            {campaign && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="rounded-2xl border border-slate-200 p-4 space-y-2">
                  <div className="text-xs font-semibold uppercase text-slate-600">
                    Campaign summary
                  </div>
                  <div className="text-sm text-slate-700">Name: {campaign.name}</div>
                  <div className="text-sm text-slate-700">Goal: {goalLabel}</div>
                  <div className="text-sm text-slate-700">Status: {campaign.status}</div>
                  <div className="text-sm text-slate-700">
                    Sent:{" "}
                    {campaign.send_started_at
                      ? formatDateTimeInTimeZone(campaign.send_started_at, salonTimeZone)
                      : "--"}
                  </div>
                  <div className="text-sm text-slate-700">
                    Scheduled:{" "}
                    {campaign.scheduled_for
                      ? formatDateTimeInTimeZone(campaign.scheduled_for, salonTimeZone)
                      : "--"}
                  </div>
                  <div className="text-sm text-slate-700">
                    Template: {template?.name || "--"}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 p-4 space-y-2">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase text-slate-600">
                    <InfoTooltip text={CAMPAIGN_TOOLTIPS.tracking} label="Tracking" />
                    Rates
                  </div>
                  <div className="text-sm text-slate-700">Open rate: {pct(metrics?.open_rate)}</div>
                  <div className="text-sm text-slate-700">Click rate: {pct(metrics?.click_rate)}</div>
                  <div className="text-sm text-slate-700">
                    Attribution window: {metrics?.attribution_window_days || 14} days
                  </div>
                  <div className="text-sm text-slate-700">
                    Total revenue: {currency.format(Number(metrics?.total_revenue || 0))}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 p-4 space-y-2">
                  <div className="text-xs font-semibold uppercase text-slate-600">
                    Salon context
                  </div>
                  <div className="text-sm text-slate-700">Salon: {salonName || "--"}</div>
                  <div className="text-sm text-slate-700">Salon ID: {salonId || "--"}</div>
                  <div className="text-sm text-slate-700">Time zone: {salonTimeZone}</div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {metricCards.map((metric) => (
                <div
                  key={metric.label}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                >
                  <div className="text-xs font-semibold uppercase text-slate-500">
                    {metric.label}
                  </div>
                  <div className="text-lg font-semibold text-slate-900">
                    {metric.value}
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 rounded-2xl border border-slate-200 p-4">
                <div className="text-sm font-semibold text-slate-900">Recipients</div>
                <div className="mt-3 overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Name</th>
                        <th className="px-3 py-2 text-left">Email</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Sent</th>
                        <th className="px-3 py-2 text-left">Opens</th>
                        <th className="px-3 py-2 text-left">Clicks</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {recipients.map((recipient) => (
                        <tr key={recipient.id}>
                          <td className="px-3 py-2 text-slate-700">
                            {recipient.full_name || "--"}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {recipient.email || "--"}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {recipient.status}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {recipient.sent_at
                              ? formatDateTimeInTimeZone(recipient.sent_at, salonTimeZone)
                              : "--"}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {recipient.open_count || 0}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {recipient.click_count || 0}
                          </td>
                        </tr>
                      ))}
                      {recipients.length === 0 && (
                        <tr>
                          <td
                            colSpan="6"
                            className="px-3 py-4 text-center text-xs text-slate-500"
                          >
                            No recipients yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <ShareToSocialPanel
                salonId={salonId}
                salonName={salonName}
                campaignId={campaign?.id}
                template={template}
                socialLinks={socialLinks}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
